<template>
  <div class="container">
    <h2 class="pb-5">Manage materials</h2>

    <div class="text-right mb-3">
      <base-button
        @click.native="add(null, null)"
        radius="tr"
        component-class="green filled"
      ><i class="fas fa-plus-circle mr-3"></i> ADD
      </base-button>
    </div>
    <div class="tree-container">
      <sl-vue-tree
        v-model="materials"
        @drop="nodeDropped"
        ref="slVueTree"
      >
        <template
          slot="title"
          slot-scope="{ node }"
        >
          <div
            @dblclick="event => edit(event, node)"
            class="title"
          >
            <span
              v-if="node.isLeaf"
              class="arrow"
              style="width: 21px !important;"
            >&nbsp;</span>
            {{ node.title }}
          </div>
        </template>
        <template
          slot="toggle"
          slot-scope="{ node }"
        >
          <span
            v-if="!node.isLeaf"
            class="arrow"
          >
            <i
              v-if="node.isExpanded"
              class="fa fa-chevron-down"
            />
            <i
              v-if="!node.isExpanded"
              class="fa fa-chevron-right"
            />
          </span>
        </template>
        <template slot="sidebar" slot-scope="{ node }">
          <span class="visible-icon" @click="event => visibility(event, node)">
            <i
              v-if="!node.data || node.data.visible !== false"
              class="fa fa-eye"
              title="Material is visible now"
            ></i>
            <i
              v-if="node.data && node.data.visible === false"
              class="fa fa-eye-slash"
              title="Material is hidden."
            ></i>
          </span>
          <span
            :title="'Add material to ' + node.title"
            class="add-node"
            @click="event => add(event, node)"
          >
            <i class="fas fa-plus"></i>
          </span>
          <span
            :class="{disabled: !node.data.can_be_changed}"
            :title="'Edit material ' + node.title"
            class="edit-node"
            @click="event => edit(event, node)"
          >
            <i class="fas fa-pen"></i>
          </span>
          <span
            :class="{disabled: !node.data.can_be_changed}"
            :title="'Remove material ' + node.title"
            class="remove-node"
            @click="event => remove(event, node)"
          >
            <i class="fas fa-ban"></i>
          </span>
        </template>
      </sl-vue-tree>
    </div>

    <show-material-modal v-if="material"></show-material-modal>

    <div class="row mt-4">
      <div class="col-6 text-left">
        <base-button radius="bl" component-class="blue" @click.native="$router.go(-1)">
          <i class="fas fa-arrow-left mr-3"></i> Back
        </base-button>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions } from 'vuex';
  import SlVueTree from 'sl-vue-tree';
  import BaseButton from '@/components/inputs/button/BaseButton';
  import { getNodeSiblings } from '@/helpers/sl-vue-tree';
  import ShowMaterialModal from './ShowMaterial';
  import 'sl-vue-tree/dist/sl-vue-tree-minimal.css';

  export default {
    data() {
      return {};
    },
    computed: {
      material: {
        get() {
          return this.$store.getters['materials/current'];
        },
        set(value) {
          this.$store.dispatch('materials/current', value);
        }
      },
      materialParent: {
        get() {
          return this.$store.getters['materials/parent'];
        },
        set(value) {
          this.$store.dispatch('materials/parent', value);
        }
      },
      materials: {
        get() {
          return this.$store.getters['materials/materialsSlVueTree'];
        },
        set(value) {
          this.$store.dispatch('materials/setMaterialsSlVueTree', value);
        }
      }
    },
    methods: {
      ...mapActions({
        saveMaterialsOrder: 'materials/saveOrder'
      }),
      canBeEdited(node) {
        if (
          typeof node.data.can_be_changed !== 'undefined' &&
          node.data.can_be_changed
        ) {
          return true;
        }

        this.$modal.show('dialog', {
          title: 'Action disabled.',
          text:
            'Edit and delete actions are disabled because this material has already been used.',
          buttons: [
            {
              title: 'Close'
            }
          ]
        });

        return false;
      },
      nodeDropped() {
        const { slVueTree } = this.$refs;
        this.selectedNode = slVueTree.getSelected()[0];
        const { parent_id, parentNode } = getNodeSiblings(this.selectedNode, slVueTree);
        let { sibling_id } = getNodeSiblings(this.selectedNode, slVueTree);

        if (parentNode) {
          const lastParentChild = parentNode.children[parentNode.children.length - 1];
          if (lastParentChild.data.id === this.selectedNode.data.id) {
            sibling_id = null;
          }
        }

        this.saveMaterialsOrder({
          materialId: this.selectedNode.data.id,
          parent_id,
          sibling_id,
        });
      },
      showDialog() {
        this.$nextTick(() => {
          this.$modal.show('show-material');
        });
      },
      visibility(event, node) {
        this.material = deepCopy(node.data);
        this.material.visible = !this.material.visible;

        this.$store
          .dispatch('materials/changeVisibility', {
            ids: [this.material.id],
            visible: this.material.visible
          })
          .then(() => {
            this.$store.dispatch('materials/materials');
            this.$snotify.success('Visibility updated.');
          })
          .catch(() => {
            this.$snotify.error('Visibility error.');
          });
      },
      edit(event, node) {
        if (this.canBeEdited(node)) {
          this.materialParent = null;
          this.material = deepCopy(node.data);
          this.showDialog();
        }
      },
      remove(event, node) {
        if (this.canBeEdited(node)) {
          this.$store.dispatch('materials/remove', node.data.id);
        }
      },
      add(event, node) {
        this.material = {
          id: null,
          name: ''
        };

        if (!event && !node) {
          this.materialParent = null;
        } else {
          this.materialParent = node;
        }
        this.showDialog();
      },
    },

    mounted() {
      this.$store.dispatch('materials/materials');
    },

    components: {
      'base-button': BaseButton,
      'sl-vue-tree': SlVueTree,
      'show-material-modal': ShowMaterialModal
    }
  };
</script>
